/**
 * Terrain-id → RGB colour palette.
 *
 * Terrain IDs are the 10 v1 terrain types from MAP.md §1.1. Colours
 * picked for a muted classical-antiquity feel — parchment-and-earth
 * reads better behind gameplay markers than saturated hues.
 * STYLE_GUIDE.md reserves the Okabe-Ito palette for factions; terrain
 * gets its own subdued set.
 *
 * v0M2.4 authored palette. First-playtest tuning candidate.
 *
 * v0M7.4c added River (id 9) to the schema as Infinity-cost (impassable
 * without a Bridge world buildable). MFix9 (2026-07-26) is what wired
 * the strategic-map generator to actually emit id-9 cells; the smooth-
 * curve edge overlay in river-render.ts renders on top of the id-9
 * cells to keep the visual style unchanged.
 */

/** RGB tuple, each channel 0-255. */
export type Rgb = readonly [number, number, number];

/** Named terrain IDs — kept in sync with scripts/generate-sample-map.py. */
export const enum Terrain {
  Plains = 0,
  Farmland = 1,
  Forest = 2,
  Hills = 3,
  Mountains = 4,
  Desert = 5,
  Swamp = 6,
  Lake = 7,
  Sea = 8,
  /** MFix9 (2026-07-26). Impassable without a Bridge world buildable. */
  River = 9,
}

/** Palette indexed by terrain id. */
export const TERRAIN_PALETTE: Readonly<Record<number, Rgb>> = {
  [Terrain.Plains]: [201, 187, 141],
  [Terrain.Farmland]: [212, 188, 108],
  [Terrain.Forest]: [92, 118, 84],
  [Terrain.Hills]: [162, 143, 108],
  [Terrain.Mountains]: [117, 106, 92],
  [Terrain.Desert]: [222, 199, 143],
  [Terrain.Swamp]: [110, 118, 98],
  [Terrain.Lake]: [110, 140, 152],
  [Terrain.Sea]: [64, 96, 128],
  // MFix9 (2026-07-26) — River. A shade darker + more saturated than
  // Lake so the flowing waterway reads distinctly from still bodies at
  // a glance. The smooth curved-line overlay from river-render.ts
  // draws on top so the underlying cell tint is mostly obscured; the
  // colour surfaces at tile edges + when zoomed out where the overlay
  // stroke thins.
  [Terrain.River]: [88, 122, 148],
};

/**
 * Relative elevation per terrain type. Used by hillshade.ts (v0M2.6d)
 * to compute per-cell shading — mountains catch NW light, sea sinks
 * into the void. Values are unitless; only the gradient between
 * neighbours matters for the shading calculation.
 */
export const TERRAIN_ELEVATION: Readonly<Record<number, number>> = {
  [Terrain.Plains]: 0.0,
  [Terrain.Farmland]: 0.05,
  [Terrain.Forest]: 0.15,
  [Terrain.Hills]: 0.5,
  [Terrain.Mountains]: 1.0,
  [Terrain.Desert]: 0.05,
  [Terrain.Swamp]: -0.05,
  [Terrain.Lake]: -0.25,
  [Terrain.Sea]: -0.45,
  // MFix9 — Rivers cut through the landscape; slightly-below-plains
  // reads as an incised waterway without pushing hillshading toward
  // lake / sea territory.
  [Terrain.River]: -0.15,
};

/**
 * Default colour returned for unknown terrain IDs — magenta so
 * anomalies are eye-obvious rather than blending in with valid terrain.
 */
export const UNKNOWN_TERRAIN_COLOR: Rgb = [255, 0, 255];

/**
 * Resolve a terrain id to its palette RGB. Returns UNKNOWN_TERRAIN_COLOR
 * for unmapped ids so the map still renders (loud) rather than throwing.
 */
export function terrainColor(id: number): Rgb {
  return TERRAIN_PALETTE[id] ?? UNKNOWN_TERRAIN_COLOR;
}

/** Elevation lookup with a safe default for unknown ids. */
export function terrainElevation(id: number): number {
  return TERRAIN_ELEVATION[id] ?? 0;
}
